import { memo, useCallback, useEffect, useState } from 'react'
import { GameLink } from 'types/games-links'
import { GameLinkType } from 'types/game-link-type'
import { getGameLinkTypes } from 'services/game-link-types'
import { Button, Form, Input, Select, Space, Spin } from 'antd'
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { addLinkToGame, deleteLinkFromGame, editLinkFromGame, getGameLinks } from 'services/games-links'
import { Game } from 'types/games'

type Props = {
    game: Game
    onSubmit: () => void
}

const GameLinksForm = memo((props: Props) => {
    const [gameLinkTypes, setGameLinkTypes] = useState<GameLinkType[]>()
    const [gameLinks, setGameLinksTypes] = useState<GameLink[]>()
    const [loading, setLoading] = useState<boolean>(true)

    const { game, onSubmit } = props

    const fetchGameLinkTypes = useCallback(() => {
        return getGameLinkTypes()
    }, [])

    const fetchGameLinks = useCallback(() => {
        return getGameLinks(game.id)
    }, [game])

    useEffect(() => {
        setLoading(true)

        Promise.all([fetchGameLinkTypes(), fetchGameLinks()])
            .then(([gameLinkTypes, gameLinks]) => {
                setGameLinkTypes(gameLinkTypes)
                setGameLinksTypes(gameLinks)
            })
            .finally(() => {
                setLoading(false)
            })
    }, [fetchGameLinkTypes, fetchGameLinks])

    const onFinish = useCallback(
        async (values: { gameLinks: GameLink[] }) => {
            setLoading(true)

            const links = values.gameLinks

            const oldIds = (gameLinks || []).reduce((acc, it) => {
                acc.set(it.id!, true)
                return acc
            }, new Map())

            const linkForCreate: GameLink[] = []
            const linkForEdit: GameLink[] = []

            links.forEach(it => {
                const { id } = it
                if (id) {
                    linkForEdit.push(it)
                    oldIds.delete(id)
                } else if (!id) {
                    linkForCreate.push(it)
                }
            })

            try {
                // @ts-ignore
                // DELETE
                for (let id of oldIds.keys()) {
                    await deleteLinkFromGame(id)
                }

                // UPDATE
                for (let i = 0; i < linkForEdit.length; i++) {
                    const link = linkForEdit[i]

                    if (link.id) {
                        await editLinkFromGame(link.id, link)
                    }
                }

                // CREATE

                for (let i = 0; i < linkForCreate.length; i++) {
                    const link = linkForCreate[i]

                    await addLinkToGame({
                        ...link,
                        gameId: game.id,
                    })
                }
            } catch {
                setLoading(false)
            }

            setLoading(false)

            onSubmit()
        },
        [gameLinks, onSubmit, game]
    )

    if (!gameLinks) {
        return <div>Loading</div>
    }

    return (
        <Spin spinning={loading} delay={300}>
            {gameLinks ? (
                <Form onFinish={onFinish} autoComplete='off'>
                    <Form.List name='gameLinks' initialValue={gameLinks}>
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Space
                                        key={key}
                                        style={{
                                            display: 'flex',
                                            marginBottom: 8,
                                        }}
                                        align='baseline'
                                    >
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'gameLinkTypeId']}
                                            required
                                            rules={[{ required: true, message: 'Missing link type' }]}
                                            style={{ minWidth: '120px' }}
                                        >
                                            <Select>
                                                {(gameLinkTypes || []).map(it => (
                                                    <Select.Option key={it.id} value={it.id}>
                                                        {it.name}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'link']}
                                            required
                                            rules={[{ required: true, message: 'Missing link' }]}
                                        >
                                            <Input placeholder='Link' />
                                        </Form.Item>
                                        <Form.Item {...restField} name={[name, 'title']}>
                                            <Input placeholder='Title' />
                                        </Form.Item>
                                        <Form.Item {...restField} name={[name, 'description']}>
                                            <Input placeholder='Description' />
                                        </Form.Item>
                                        <MinusCircleOutlined onClick={() => remove(name)} />
                                    </Space>
                                ))}
                                <Form.Item>
                                    <Button type='dashed' onClick={() => add()} block icon={<PlusOutlined />}>
                                        Add field
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>
                    <Form.Item>
                        <Button type='primary' htmlType='submit'>
                            Submit
                        </Button>
                    </Form.Item>
                </Form>
            ) : null}
        </Spin>
    )
})

export default GameLinksForm
